import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GoToDashboardButton from './GoToDashboardButton';
import ReportBuilderPage from './ReportBuilderPage';
import styles from './OperationsCenter.module.css';
import { fetchReportSnapshot } from '../api/ops';

function formatDate(value, language) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(language || undefined);
}

export default function OperationsCenter({ currentUser }) {
  return <ReportBuilderPage currentUser={currentUser} entryMode="operations" />;
}

export function OperationsSnapshotPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await fetchReportSnapshot(slug);
        if (!cancelled) setSnapshot(result.snapshot || null);
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || 'Could not load the snapshot.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const payload = snapshot?.payload || {};
  const findings = payload.warnings || [];

  return (
    <div className={styles.container}>
      <GoToDashboardButton />
      <div className={styles.content}>
        <header className={styles.hero}>
          <div>
            <p className={styles.eyebrow}>{t('Frozen snapshot')}</p>
            <h1 className={styles.title}>{snapshot?.title || t('Snapshot')}</h1>
            <p className={styles.description}>{t('This is a point-in-time Smarti snapshot that preserves the chosen report scope and key metrics.')}</p>
          </div>
        </header>

        {loading ? <div className={styles.noticeCard}>{t('Loading snapshot...')}</div> : null}
        {error ? <div className={`${styles.noticeCard} ${styles.errorNotice}`}>{error}</div> : null}
        {snapshot ? (
          <section className={styles.section}>
            <div className={styles.metricGrid}>
              <MetricCard label={t('Template')} value={snapshot.templateKey} description={t('Snapshot source template.')} />
              <MetricCard label={t('Created')} value={formatDate(snapshot.createdAt, i18n.language)} description={t('Snapshot creation time.')} />
              <MetricCard label={t('Date range')} value={snapshot.dateRangeLabel || t('Current scope')} description={t('Captured scope label.')} />
              <MetricCard label={t('Health score')} value={payload.accountHealth?.score || 0} description={t('Account-health signal at snapshot time.')} />
            </div>
            <div className={styles.reportingGrid}>
              <article className={styles.panelCard}>
                <h3>{t('Captured reconciliation')}</h3>
                <div className={styles.keyValueList}>
                  <KeyValue label={t('Uploaded files')} value={payload.reconciliation?.uploadedFiles || 0} />
                  <KeyValue label={t('Imported files')} value={payload.reconciliation?.importedFiles || 0} />
                  <KeyValue label={t('Blocked files')} value={payload.reconciliation?.blockedFiles || 0} />
                  <KeyValue label={t('Chartable rows')} value={payload.reconciliation?.chartableStepRows || 0} />
                </div>
              </article>
              <article className={styles.panelCard}>
                <h3>{t('Captured findings')}</h3>
                <ul className={styles.list}>
                  {findings.map((item) => <li key={item}>{item}</li>)}
                  {!findings.length ? <li>{t('No findings were saved in this snapshot.')}</li> : null}
                </ul>
              </article>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function MetricCard({ label, value, description }) {
  return (
    <article className={styles.metricCard}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{description}</p>
    </article>
  );
}

function KeyValue({ label, value }) {
  return (
    <div className={styles.keyValueRow}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
